import {
  ApdmCampaignDefinition,
  ApdmCampaignPlaceholder,
  ApdmCampaignPreviewResult,
  ApdmCampaignStatus,
  ApdmCampaignVariantApplicability,
  ApdmCampaignVariantInput,
  ApdmCampaignView,
  ApdmCampaignWizardStep,
  ApdmCampaignWizardStepInput,
} from '@euler/model/apdm';

const placeholders: ApdmCampaignPlaceholder[] = [
  'customerName',
  'plateNo',
  'dueItems',
  'lastVisitDate',
  'storeName',
];
export function wizardVariant(
  text: string,
  applicability: ApdmCampaignVariantApplicability | null,
): ApdmCampaignVariantInput {
  const names = [...text.matchAll(/\{([^{}]+)\}/g)].map(match => match[1]);
  return {
    text,
    applicability,
    requiredFields: placeholders.filter(field => names.includes(field)),
  };
}
export function wizardDefinition(
  view: ApdmCampaignView,
): ApdmCampaignDefinition {
  const {
    title,
    kind,
    mode,
    audience,
    content,
    couponCampaignId,
    posterTemplateAttachmentId,
    reviewEnabled,
    schedule,
    policy,
    variants,
  } = view;
  return {
    title,
    kind,
    mode,
    audience,
    content,
    couponCampaignId,
    posterTemplateAttachmentId,
    reviewEnabled,
    schedule,
    policy,
    variants,
  };
}
export function wizardStepInput(
  view: ApdmCampaignView,
  draft: ApdmCampaignDefinition,
  step: ApdmCampaignWizardStep,
): ApdmCampaignWizardStepInput {
  const ref = { campaignId: view.id, expectedVersion: view.version };
  switch (step) {
    case ApdmCampaignWizardStep.Basics:
      return {
        ...ref,
        step,
        data: { title: draft.title, kind: draft.kind, mode: draft.mode },
      };
    case ApdmCampaignWizardStep.Audience:
      return { ...ref, step, data: draft.audience };
    case ApdmCampaignWizardStep.Content:
      return {
        ...ref,
        step,
        data: {
          content: draft.content,
          couponCampaignId: draft.couponCampaignId,
          posterTemplateAttachmentId: draft.posterTemplateAttachmentId,
          variants: draft.variants,
        },
      };
    case ApdmCampaignWizardStep.Schedule:
      return {
        ...ref,
        step,
        data: {
          schedule: draft.schedule,
          policy: draft.policy,
          reviewEnabled: draft.reviewEnabled,
        },
      };
  }
}
export function applyWizardLocalStep(
  draft: ApdmCampaignDefinition,
  input: ApdmCampaignWizardStepInput,
): ApdmCampaignDefinition {
  return input.step === ApdmCampaignWizardStep.Audience
    ? { ...draft, audience: input.data }
    : { ...draft, ...input.data };
}
export function assertWizardStep(input: ApdmCampaignWizardStepInput): void {
  const checkNumbers = (value: unknown): void => {
    if (
      typeof value === 'number' &&
      (!Number.isSafeInteger(value) || value < 0)
    )
      throw new Error('Invalid numeric wizard field');
    if (Array.isArray(value)) value.forEach(checkNumbers);
    else if (value && typeof value === 'object')
      Object.values(value).forEach(checkNumbers);
  };
  checkNumbers(input.data);
  if (input.step === ApdmCampaignWizardStep.Content) {
    if (input.data.variants.length < 3 || input.data.variants.length > 5)
      throw new Error('Three to five variants are required');
    for (const variant of input.data.variants) {
      if (
        !variant.text.trim() ||
        [...variant.text.matchAll(/\{([^{}]+)\}/g)].some(
          match => !placeholders.includes(match[1] as ApdmCampaignPlaceholder),
        )
      )
        throw new Error('Invalid variant text or placeholder');
    }
  }
}
export function wizardPreviewCurrent(
  campaign: ApdmCampaignView | null,
  preview: ApdmCampaignPreviewResult | null,
  dirty: readonly ApdmCampaignWizardStep[],
): boolean {
  return (
    !!campaign &&
    !!preview &&
    dirty.length === 0 &&
    campaign.id === preview.campaign.id &&
    campaign.version === preview.campaign.version
  );
}
export function wizardCanConfirm(
  campaign: ApdmCampaignView | null,
  preview: ApdmCampaignPreviewResult | null,
  dirty: readonly ApdmCampaignWizardStep[],
  authorized: boolean,
  reviewed: boolean,
): boolean {
  return (
    authorized &&
    reviewed &&
    campaign?.status === ApdmCampaignStatus.Draft &&
    campaign.variants.length >= 3 &&
    campaign.variants.length <= 5 &&
    wizardPreviewCurrent(campaign, preview, dirty) &&
    !preview?.samples.some(sample => sample.kind === 'unavailable') &&
    ((preview?.samples.length === 0 &&
      preview.counts.total === preview.counts.excluded) ||
      (!!preview?.variantExamples &&
        preview.variantExamples.length === campaign?.variants.length &&
        preview.variantExamples.every(
          (example, index) =>
            example.variantIndex === index &&
            example.kind === 'ready' &&
            example.text.trim().length > 0,
        )))
  );
}
